import type { User } from '../types';
import { AdminView } from '../enums';

const CANONICAL_PERMISSION_LIST = [
  // Page permissions
  'DASHBOARD',
  'CAMPAIGNS',
  'SUBMISSIONS',
  'PROPOSALS',
  'TABLE',
  'REPORTS',
  'ANALYTICS',
  'COMMUNITY',
  'MENU_BUILDER',
  'SETTINGS',
  'USERS',
  'PROFILE',
  'TRACE',
  'MANAGER_DASHBOARD',
  'MANAGERS_DASHBOARD',
  // Action permissions
  'SUBMIT_SUBMISSIONS',
  'SUBMIT_PROPOSALS',
  'SUBMIT_CAMPAIGNS',
] as const;

type CanonicalPermission = (typeof CANONICAL_PERMISSION_LIST)[number];

const PERMISSION_ALIASES: Record<string, CanonicalPermission> = {
  SUBMISSIONS_NEW: 'SUBMISSIONS',
  CANSUBMITSUBMISSIONS: 'SUBMIT_SUBMISSIONS',
  CANSUBMITPROPOSALS: 'SUBMIT_PROPOSALS',
  CANSUBMITCAMPAIGNS: 'SUBMIT_CAMPAIGNS',
  MANAGER_DASHBOARD_VIEW: 'MANAGER_DASHBOARD',
  MANAGER_DASHBOARD_HISTORY: 'MANAGER_DASHBOARD',
  MANAGER_HISTORY: 'MANAGER_DASHBOARD',
  MANAGER_DASHBOARD_PAGE: 'MANAGER_DASHBOARD',
};

const normalizeKey = (raw: string) => raw.trim().replace(/[^a-zA-Z0-9]+/g, '_').toUpperCase();

const normalizeLegacyPermissions = (perms: Array<string | AdminView> | undefined | null): AdminView[] => {
  if (!Array.isArray(perms)) return [];
  const found = new Set<CanonicalPermission>();

  perms.forEach((p) => {
    if (typeof p === 'string') {
      const normalized = normalizeKey(p);
      const aliasMatch = PERMISSION_ALIASES[normalized] || PERMISSION_ALIASES[p.toLowerCase()];
      if (aliasMatch && CANONICAL_PERMISSION_LIST.includes(aliasMatch)) {
        found.add(aliasMatch);
        return;
      }
      if (CANONICAL_PERMISSION_LIST.includes(normalized as CanonicalPermission)) {
        found.add(normalized as CanonicalPermission);
      }
      return;
    }
    const val = p as AdminView;
    if (val && CANONICAL_PERMISSION_LIST.includes(val)) {
      found.add(val as CanonicalPermission);
    }
  });

  return CANONICAL_PERMISSION_LIST.filter((p) => found.has(p as CanonicalPermission)) as AdminView[];
};

export const canonicalizePermissions = (value: any): AdminView[] => {
  const normalized = normalizeLegacyPermissions(value);
  return normalized;
};

export const hasPerm = (user: Pick<User, 'permissions'> | null | undefined, perm: AdminView | string): boolean => {
  if (!user || !Array.isArray(user.permissions)) return false;
  const canonical = canonicalizePermissions(user.permissions);
  if (canonical.includes(perm as AdminView)) return true;
  const backwardCompatMap: Record<string, AdminView> = {
    [AdminView.SUBMIT_SUBMISSIONS]: AdminView.SUBMISSIONS,
    [AdminView.SUBMIT_PROPOSALS]: AdminView.PROPOSALS,
    [AdminView.SUBMIT_CAMPAIGNS]: AdminView.CAMPAIGNS,
  };
  const fallbackPerm = backwardCompatMap[perm as string];
  if (fallbackPerm && canonical.includes(fallbackPerm)) {
    return true;
  }
  return false;
};

export const hasAnyPerm = (user: Pick<User, 'permissions'> | null | undefined, perms: Array<AdminView | string>): boolean => {
  return perms.some((p) => hasPerm(user, p));
};
